"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { MapPin, Quote, Activity } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [];
const GOOGLE_REVIEWS_SHORTCODE = "[trustindex no-registration=google]";
export default function GlobalSuccessStories() {
  const {
    ref,
    isVisible
    // the reviews widget makes this section taller than a few viewports, and the
    // default 0.2 threshold can then never be met, leaving the whole section at
    // opacity-0 — reveal as soon as any part of it intersects instead
  } = useRevealOnScroll({
    threshold: 0
  });
  const reviewsRef = React.useRef<HTMLDivElement>(null);
  const shortcodeRef = React.useRef<HTMLDivElement>(null);

  // WordPress parks the expanded widget in a <wrapper id="[the shortcode]">
  // store node after the React root, which the theme's content.compiler.js is
  // meant to swap back in over the shortcode text. When that does not happen the
  // widget is left stranded at the bottom of the page, so adopt it here instead.
  React.useEffect(() => {
    const target = reviewsRef.current;
    const source = shortcodeRef.current;
    if (!target || !source) return;
    const adopt = () => {
      // the compiler may have already swapped the text for the widget
      if (source.querySelector(".ti-widget") || target.querySelector(".ti-widget")) {
        return true;
      }
      // getElementById matches the id verbatim, so the brackets need no escaping
      const store = document.getElementById(GOOGLE_REVIEWS_SHORTCODE);
      if (!store) return false;

      // move the nodes Trustindex has already initialised rather than cloning
      // them, then hide the shortcode text that never got replaced
      while (store.firstChild) target.appendChild(store.firstChild);
      store.remove();
      source.hidden = true;
      return true;
    };
    if (adopt()) return;

    // give the content compiler its own chance before stepping in
    const timer = setTimeout(adopt, 1500);
    return () => clearTimeout(timer);
  }, []);
  return <section data-section-id="26769" id="client-whispers" ref={ref} className={`bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-[hsl(45,30%,60%)]/5 rounded-lg blur-[120px]" />
        <div className="absolute bottom-[10%] right-[5%] w-[30%] h-[30%] bg-[hsl(0,20%,94%)]/5 rounded-lg blur-[100px]" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="mb-12 md:mb-20 border-b border-border/20 pb-6">
          
          <h3 className="text-3xl md:text-5xl font-serif font-light text-foreground italic">Client Whispers</h3>
        </div>

        <Carousel opts={{
        loop: true,
        align: "start"
      }} className="w-full">
          <CarouselContent>
            {testimonials.map((item, i) => <CarouselItem key={item.id} data-index={i}>
                <div className="flex flex-col gap-8 md:gap-12 max-w-5xl mx-auto">
                  <div className="relative">
                    <Quote className="absolute -top-6 -left-6 md:-top-10 md:-left-10 w-12 h-12 md:w-20 md:h-20 text-[hsl(45,30%,60%)]/20 rotate-180" />
                    <blockquote className="relative z-10">
                      
                    </blockquote>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-t border-border/20 pt-8">
                    <div className="flex items-center gap-4">
                      <SafeHydrate><Avatar className="w-14 h-14 md:w-16 md:h-16 border-2 border-[hsl(45,30%,60%)]/20">
                        <AvatarImage src={item.avatar} alt={item.name} />
                        <AvatarFallback className="bg-[hsl(0,20%,94%)] text-foreground text-lg font-serif">
                          {item.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <div>
                        <div className="font-serif text-xl text-foreground">
                          {item.name}
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground uppercase tracking-widest text-[10px] mt-0.5">
                          <MapPin className="w-3.5 h-3.5 text-[hsl(45,30%,60%)]" />
                          <span>{item.location}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <Badge variant="secondary" className="px-4 py-1.5 text-[10px] uppercase tracking-[0.15em] font-medium rounded-lg bg-[hsl(0,20%,94%)] text-foreground hover:bg-[hsl(0,20%,94%)]/80 border-0">
                        <Activity className="w-3.5 h-3.5 mr-2 text-[hsl(45,30%,60%)]" />
                        {item.recovery}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>

          {}
          
        </Carousel>

        <div className="mt-16 w-full">
          <div ref={reviewsRef} />
          <div ref={shortcodeRef}>
            [trustindex no-registration=google]
          </div>
        </div>
      </div>
    </section>;
}